import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Building2, Loader2, Pencil } from "lucide-react";

function EditDialog({ prop, onSaved, onClose }) {
  const [form, setForm] = useState({
    name: prop.name || "", owner_name: prop.owner_name || "", billing_phone: prop.billing_phone || "",
    billing_email: prop.billing_email || "", address: prop.address || prop.location || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/properties/${prop.id}`, { ...form, location: form.address });
      toast.success("Property updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Edit property detail</DialogTitle>
        <DialogDescription>Perbarui data owner &amp; kontak property.</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="space-y-3">
        <div className="space-y-1.5"><Label>Property name</Label><Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="edit-prop-name" /></div>
        <div className="space-y-1.5"><Label>Owner name</Label><Input value={form.owner_name} onChange={(e) => upd("owner_name", e.target.value)} data-testid="edit-prop-owner" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Phone</Label><Input value={form.billing_phone} onChange={(e) => upd("billing_phone", e.target.value)} data-testid="edit-prop-phone" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.billing_email} onChange={(e) => upd("billing_email", e.target.value)} data-testid="edit-prop-email" /></div>
        </div>
        <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={(e) => upd("address", e.target.value)} data-testid="edit-prop-address" /></div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" className="flex-1" disabled={saving} data-testid="save-prop-detail">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </div>
      </form>
    </DialogContent>
  );
}

export default function PropertyRegistryPage() {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/properties"); setProps(data.properties); }
    catch { toast.error("Failed to load properties"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#1F1B16] dark-scroll text-slate-100">
      <AppNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2 text-white"><Building2 className="h-6 w-6 text-indigo-400" /> Property Registry</h1>
          <p className="text-sm text-slate-400">Daftar property live — detail owner otomatis dibuat dari kontrak partner yang sudah di-sign</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-indigo-400" /></div>
        ) : props.length === 0 ? (
          <p className="text-center text-slate-500 py-20">Belum ada property.</p>
        ) : (
          <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="property-registry-table">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 text-left">
                    <th className="p-3 font-medium">Owner name</th>
                    <th className="p-3 font-medium">Property name</th>
                    <th className="p-3 font-medium">Phone</th>
                    <th className="p-3 font-medium">Email</th>
                    <th className="p-3 font-medium">Address</th>
                    <th className="p-3 font-medium">Source</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {props.map((p) => (
                    <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`registry-row-${p.id}`}>
                      <td className="p-3 text-slate-200">{p.owner_name || "—"}</td>
                      <td className="p-3 font-medium text-white">{p.name}</td>
                      <td className="p-3 text-slate-300">{p.billing_phone || "—"}</td>
                      <td className="p-3 text-slate-300">{p.billing_email || "—"}</td>
                      <td className="p-3 text-slate-300">{p.address || p.location || "—"}</td>
                      <td className="p-3">{p.source === "partner" ? <Badge className="bg-indigo-100 text-indigo-700">From contract</Badge> : <Badge variant="secondary">Manual</Badge>}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setEditing(p)} data-testid={`edit-prop-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          {editing && <EditDialog prop={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
        </Dialog>
      </main>
    </div>
  );
}
